import React, { useState, useEffect, useCallback } from "react";
import api, { API, getToken } from "../lib/api";
import { FileXls, Download, FloppyDisk } from "@phosphor-icons/react";

function CompanyInfoCard() {
  const [company, setCompany] = useState({ legal_name: "", address: "", city: "", state: "CA", zip: "", phone: "" });
  const [saved, setSaved] = useState(false);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.get("/company").then(({ data }) => {
      if (data && Object.keys(data).length) setCompany((c) => ({ ...c, ...data }));
    });
  }, []);

  const save = async () => {
    setBusy(true); setSaved(false);
    try {
      await api.put("/company", company);
      setSaved(true);
      setTimeout(() => setSaved(false), 2500);
    } finally {
      setBusy(false);
    }
  };

  const set = (k) => (e) => setCompany((c) => ({ ...c, [k]: e.target.value }));

  return (
    <div className="bg-white border border-stone-200 p-6 shadow-sm shadow-stone-900/5 space-y-4" data-testid="company-info-card">
      <div>
        <h2 className="text-xl font-semibold tracking-tight" style={{ fontFamily: "'Fraunces', serif" }}>Company Info (Employer)</h2>
        <p className="text-xs text-stone-500">Required on every §226 wage statement — legal name & address of the employer.</p>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <div className="sm:col-span-2">
          <label className="text-xs font-bold uppercase tracking-[0.1em] block mb-1">Legal Company Name</label>
          <input data-testid="company-legal_name-input" value={company.legal_name ?? ""} placeholder="Vinedresser Vineyard Management, Inc." onChange={set("legal_name")} className="w-full h-11 px-3 border border-stone-200" />
        </div>
        <div className="sm:col-span-2">
          <label className="text-xs font-bold uppercase tracking-[0.1em] block mb-1">Street Address</label>
          <input data-testid="company-address-input" value={company.address ?? ""} placeholder="1234 Silverado Trail" onChange={set("address")} className="w-full h-11 px-3 border border-stone-200" />
        </div>
        <div>
          <label className="text-xs font-bold uppercase tracking-[0.1em] block mb-1">City</label>
          <input data-testid="company-city-input" value={company.city ?? ""} placeholder="Napa" onChange={set("city")} className="w-full h-11 px-3 border border-stone-200" />
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="text-xs font-bold uppercase tracking-[0.1em] block mb-1">State</label>
            <input data-testid="company-state-input" value={company.state ?? ""} placeholder="CA" onChange={set("state")} className="w-full h-11 px-3 border border-stone-200" />
          </div>
          <div>
            <label className="text-xs font-bold uppercase tracking-[0.1em] block mb-1">ZIP</label>
            <input data-testid="company-zip-input" value={company.zip ?? ""} placeholder="94558" onChange={set("zip")} className="w-full h-11 px-3 border border-stone-200" />
          </div>
        </div>
        <div>
          <label className="text-xs font-bold uppercase tracking-[0.1em] block mb-1">Phone</label>
          <input data-testid="company-phone-input" value={company.phone ?? ""} placeholder="(707) 555-0100" onChange={set("phone")} className="w-full h-11 px-3 border border-stone-200" />
        </div>
      </div>
      <div className="flex items-center gap-3">
        <button data-testid="save-company-btn" onClick={save} disabled={busy}
          className="h-12 px-5 bg-[#1F3D34] text-white font-bold uppercase text-xs border border-stone-200 flex items-center gap-2 disabled:opacity-50">
          <FloppyDisk weight="bold" size={18} /> {busy ? "Saving…" : "Save Company Info"}
        </button>
        {saved && <span className="text-sm font-semibold text-[#7C9673]" data-testid="company-saved-msg">Saved ✓</span>}
      </div>
    </div>
  );
}

export default function Export() {
  const today = new Date().toISOString().slice(0, 10);
  const twoWeekAgo = new Date(Date.now() - 14 * 86400000).toISOString().slice(0, 10);
  const [start, setStart] = useState(twoWeekAgo);
  const [end, setEnd] = useState(today);
  const [busy, setBusy] = useState(false);
  const [summary, setSummary] = useState(null);
  const [wage, setWage] = useState(null);
  const [kind, setKind] = useState("employee"); // "employee" | "equipment" | "wage226"

  const loadSummary = async () => {
    const { data } = await api.get(`/reports/summary?start_date=${start}&end_date=${end}`);
    setSummary(data);
  };

  const loadWage = useCallback(async () => {
    const { data } = await api.get(`/reports/wage-statement?start_date=${start}&end_date=${end}`);
    setWage(data);
  }, [start, end]);

  useEffect(() => {
    if (kind === "wage226") loadWage();
  }, [kind, loadWage]);

  const download = async () => {
    setBusy(true);
    try {
      const token = getToken();
      const path = kind === "equipment" ? "export/equipment"
        : kind === "wage226" ? "export/wage-statement"
        : "export/quickbooks";
      const res = await fetch(`${API}/${path}?start_date=${start}&end_date=${end}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error("Export failed");
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = kind === "equipment"
        ? `equipment_timecards_${start}_${end}.xlsx`
        : kind === "wage226"
        ? `wage_statement_226_${start}_${end}.xlsx`
        : `quickbooks_payroll_${start}_${end}.xlsx`;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
    } catch (e) {
      alert(e.message);
    } finally {
      setBusy(false);
    }
  };

  const money = (v) => `$${Number(v || 0).toFixed(2)}`;

  return (
    <div className="space-y-6" data-testid="export-page">
      <div>
        <h1 className="text-4xl sm:text-5xl font-semibold tracking-tight" style={{ fontFamily: "'Fraunces', serif" }}>Payroll Exports</h1>
        <p className="text-sm font-medium tracking-wide uppercase text-stone-500">QuickBooks-ready XLSX & CA §226 wage statements</p>
      </div>

      <div className="bg-white border border-stone-200 p-6 shadow-sm shadow-stone-900/5 space-y-4">
        <div>
          <label className="text-sm font-bold uppercase tracking-[0.1em] block mb-2">Export Type</label>
          <div className="inline-flex flex-wrap border border-stone-200 p-1" data-testid="export-kind-tabs">
            {[["employee", "Employee Time Cards"], ["equipment", "Equipment Time Cards"], ["wage226", "§226 Wage Statement"]].map(([k, label]) => (
              <button
                key={k}
                data-testid={`export-kind-${k}`}
                onClick={() => setKind(k)}
                className={`h-11 px-5 font-bold uppercase text-xs tracking-wide transition-colors ${
                  kind === k ? "bg-[#1F3D34] text-white" : "bg-transparent text-stone-600 hover:text-[#1F3D34]"
                }`}
              >
                {label}
              </button>
            ))}
          </div>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <label className="text-sm font-bold uppercase tracking-[0.1em] block mb-1">{kind === "equipment" ? "Start Date" : "Pay Period Start"}</label>
            <input data-testid="export-start" type="date" value={start} onChange={(e) => setStart(e.target.value)} className="w-full h-14 px-4 border border-stone-200 text-lg" />
          </div>
          <div>
            <label className="text-sm font-bold uppercase tracking-[0.1em] block mb-1">{kind === "equipment" ? "End Date" : "Pay Period End"}</label>
            <input data-testid="export-end" type="date" value={end} onChange={(e) => setEnd(e.target.value)} className="w-full h-14 px-4 border border-stone-200 text-lg" />
          </div>
        </div>
        <div className="flex flex-wrap gap-3">
          {kind === "wage226" ? (
            <button data-testid="preview-wage-btn" onClick={loadWage} className="h-14 px-6 bg-white font-bold uppercase border border-stone-200 shadow-sm shadow-stone-900/5">Refresh Preview</button>
          ) : (
            <button data-testid="preview-summary-btn" onClick={loadSummary} className="h-14 px-6 bg-white font-bold uppercase border border-stone-200 shadow-sm shadow-stone-900/5">Preview Summary</button>
          )}
          <button data-testid="export-qb-btn" onClick={download} disabled={busy}
            className="h-14 px-6 bg-[#1F3D34] text-white font-bold uppercase border border-stone-200 shadow-sm shadow-stone-900/5 flex items-center gap-2 disabled:opacity-50">
            <FileXls weight="bold" size={22} /> {busy ? "Generating…" : (kind === "equipment" ? "Download Equipment XLSX" : kind === "wage226" ? "Download §226 XLSX" : "Download Payroll XLSX")} <Download weight="bold" />
          </button>
        </div>
      </div>

      {kind === "wage226" && <CompanyInfoCard />}

      {kind !== "wage226" && summary && (
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4" data-testid="export-summary">
          {[
            ["Total Hrs", summary.total_hours],
            ["Regular", summary.total_regular],
            ["Overtime", summary.total_overtime],
            ["Double Time", summary.total_doubletime],
            ["Entries", summary.entries_count],
            ["Employees", summary.employees_count],
            ["Meal Penalties", summary.meal_penalties],
            ["Rest Penalties", summary.rest_penalties],
          ].map(([l, v]) => (
            <div key={l} className="bg-white border border-stone-200 p-4 shadow-sm shadow-stone-900/5">
              <div className="text-xs font-medium tracking-wide uppercase text-stone-500">{l}</div>
              <div className="text-3xl font-black tracking-tighter" style={{ fontFamily: "'Fraunces', serif" }}>{v}</div>
            </div>
          ))}
        </div>
      )}

      {kind === "wage226" && wage && (
        <div className="bg-white border border-stone-200 shadow-sm shadow-stone-900/5 overflow-x-auto" data-testid="wage-preview">
          <div className="p-4 border-b border-stone-200">
            <div className="font-semibold" style={{ fontFamily: "'Fraunces', serif" }}>
              {wage.company?.legal_name || "Set Company Info above"} — Pay Period {wage.pay_period.start} to {wage.pay_period.end}
            </div>
            <div className="text-xs text-stone-500">Itemized per Cal. Labor Code §226(a). Deductions & net wages handled in QuickBooks. {wage.totals.employees_count} employees.</div>
          </div>
          <table className="min-w-full text-sm">
            <thead className="bg-stone-50 text-left text-[11px] uppercase tracking-wide text-stone-500">
              <tr>
                <th className="p-2">Employee</th>
                <th className="p-2">ID (last 4)</th>
                <th className="p-2 text-right">Reg Hrs</th>
                <th className="p-2 text-right">Reg Rate</th>
                <th className="p-2 text-right">OT Hrs</th>
                <th className="p-2 text-right">OT Rate</th>
                <th className="p-2 text-right">DT Hrs</th>
                <th className="p-2 text-right">DT Rate</th>
                <th className="p-2 text-right">Total Hrs</th>
                <th className="p-2 text-right">Gross Wages</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-stone-100">
              {wage.rows.map((r) => (
                <tr key={r.employee_id} data-testid={`wage-row-${r.employee_id}`}>
                  <td className="p-2 font-medium">{r.employee_name}</td>
                  <td className="p-2 font-mono text-xs">{r.id_last4}</td>
                  <td className="p-2 text-right">{r.regular_hours}</td>
                  <td className="p-2 text-right">{money(r.regular_rate)}</td>
                  <td className="p-2 text-right">{r.overtime_hours}</td>
                  <td className="p-2 text-right">{money(r.overtime_rate)}</td>
                  <td className="p-2 text-right">{r.doubletime_hours}</td>
                  <td className="p-2 text-right">{money(r.doubletime_rate)}</td>
                  <td className="p-2 text-right font-semibold">{r.total_hours}</td>
                  <td className="p-2 text-right font-semibold">{money(r.gross_wages)}</td>
                </tr>
              ))}
              {wage.rows.length === 0 && (
                <tr><td colSpan={10} className="p-4 text-center text-stone-400">No time entries in this pay period.</td></tr>
              )}
            </tbody>
            {wage.rows.length > 0 && (
              <tfoot className="bg-stone-50 font-bold">
                <tr>
                  <td className="p-2" colSpan={2}>TOTAL</td>
                  <td className="p-2 text-right">{wage.totals.regular_hours}</td>
                  <td className="p-2"></td>
                  <td className="p-2 text-right">{wage.totals.overtime_hours}</td>
                  <td className="p-2"></td>
                  <td className="p-2 text-right">{wage.totals.doubletime_hours}</td>
                  <td className="p-2"></td>
                  <td className="p-2 text-right">{wage.totals.total_hours}</td>
                  <td className="p-2 text-right">{money(wage.totals.gross_wages)}</td>
                </tr>
              </tfoot>
            )}
          </table>
        </div>
      )}

      {kind === "equipment" ? (
        <div className="bg-[#D8C7A1] border border-stone-200 p-4 text-sm font-medium">
          <b className="tracking-wide">Equipment Time Cards:</b> One row per piece of equipment per shift — each piece is credited the employee's hours and tied to the job and ranch/client they worked. Multiple machines on the same shift each get their own line. Includes equipment number/code, name, type and hours, plus a Totals-by-Equipment sheet.
        </div>
      ) : kind === "wage226" ? (
        <div className="bg-[#D8C7A1] border border-stone-200 p-4 text-sm font-medium">
          <b className="tracking-wide">CA Labor Code §226(a):</b> Attach this weekly to your QuickBooks payroll report. It itemizes total hours worked, all hourly rates in effect (regular, overtime 1.5×, double-time 2×) with hours at each, gross wages, employee name & ID, pay-period dates, and the employer's legal name/address. Deductions and net wages are handled in QuickBooks. Set your Company Info above so the employer block is complete.
        </div>
      ) : (
        <div className="bg-[#D8C7A1] border border-stone-200 p-4 text-sm font-medium">
          <b className="tracking-wide">QB Import:</b> In QuickBooks Online, go to Payroll → Time Activities → Import. Use the downloaded XLSX. Columns include Employee, Date, Regular/OT/Double-Time Hours, and Customer:Job for cost coding.
        </div>
      )}
    </div>
  );
}
